import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate } from './router.js';
import { useLang, useT, type Lang } from './i18n.js';

/**
 * The staff app's chrome, copied from the prototype (prototype/foundation.css §4, foundation.js renderShell):
 * navy top bar with ⌘K search and the account menu, navy left nav with icons, a tab bar at the bottom under 900px,
 * page tabs under the page title, and a Light / Dark / System theme.
 */

/* Icons: the prototype's 24px stroke set (foundation.js ICONS). */
const ICONS: Record<string, string> = {
  scissors: '<circle cx="6" cy="6" r="3"/><path d="M8.12 8.12 12 12"/><path d="M20 4 8.12 15.88"/><circle cx="6" cy="18" r="3"/><path d="M14.8 14.8 20 20"/>',
  video: '<path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/>',
  bell: '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>',
  inbox: '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>',
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m17 8-5-5-5 5"/><path d="M12 3v12"/>',
  folder: '<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>',
  calendar: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  'user-check': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="m16 11 2 2 4-4"/>',
  'user-plus': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/>',
  settings: '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>',
  'trending-up': '<path d="m22 7-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/>',
  wallet: '<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>',
  'file-text': '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"/><path d="M14 2v5h5M16 13H8M16 17H8M10 9H8"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>',
  moon: '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>',
  monitor: '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  more: '<circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/>',
  x: '<path d="M18 6 6 18M6 6l12 12"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  'log-out': '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>',
  'arrow-right': '<path d="M5 12h14M12 5l7 7-7 7"/>',
  stopwatch: '<circle cx="12" cy="14" r="8"/><path d="M10 2h4M12 2v4M19 7l1.5-1.5"/><path class="sw-hand" d="M12 14V10"/>',
  'pop-out': '<path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  clock: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  play: '<path d="M6 3l14 9-14 9V3z"/>',
  'chevron-down': '<path d="m6 9 6 6 6-6"/>',
  lock: '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  stop: '<rect x="5" y="5" width="14" height="14" rx="2"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
  message: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  star: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z"/>',
  globe: '<circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  ticket: '<path d="M2 9a3 3 0 0 0 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 0 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2M13 17v2M13 11v2"/>',
  alert: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4M12 17h.01"/>',
  bulb: '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5M9 18h6M10 22h4"/>',
  'external-link': '<path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  briefcase: '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  eye: '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  film: '<rect x="2" y="2" width="20" height="20" rx="2.18"/><path d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 17h5M17 7h5"/>',
  phone: '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>',
  'map-pin': '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
  image: '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21"/>'
};

/** Editing and camera read faster as icons than as words (owner 2026-10-20); other roles stay words. */
const ROLE_ICON: Record<string, string> = { editor: 'scissors', camera: 'video' };

/** A role as its icon, with the word as tooltip and for screen readers; a role without an icon shows its word. */
export function RoleIcon(p: { role: string; label: string }) {
  const icon = ROLE_ICON[p.role];
  if (!icon) return <span class="role-word">{p.label}</span>;
  return (
    <span class={`role-ico role-ico--${p.role}`} title={p.label}>
      <Icon name={icon} />
      <span class="sr-only">{p.label}</span>
    </span>
  );
}

export function Icon(p: { name: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" dangerouslySetInnerHTML={{ __html: ICONS[p.name] ?? '' }} />
  );
}

/* ---------- theme ---------- */
export type Theme = 'light' | 'dark' | 'system';
const THEME_KEY = 'kff.theme';

export function readTheme(): Theme {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(t: Theme): void {
  const root = document.documentElement;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
  try {
    if (t === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, t);
  } catch {
    /* private mode – the choice lasts for this page only */
  }
}

/* ---------- page tabs (rendered by PageHead under the title) ---------- */
export interface Tab {
  path: string;
  label: string;
  active: boolean;
  /** rarely used: in the "More" menu after the other tabs, unless it is the open one */
  more?: boolean;
}
export const TabsContext = createContext<{ label: string; tabs: Tab[]; more?: string } | null>(null);

export function PageTabs() {
  const t = useContext(TabsContext);
  const menu = useRef<HTMLDetailsElement>(null);
  if (!t || t.tabs.length < 2) return null;
  const go = (x: Tab) => (e: Event) => {
    e.preventDefault();
    if (menu.current) menu.current.open = false;
    navigate(x.path);
  };
  const extra = t.tabs.filter((x) => x.more && !x.active);
  return (
    <nav class={`tabs${extra.length ? '' : ' tabs--one-row'}`} aria-label={t.label} data-tour="tabs">
      {t.tabs
        .filter((x) => !x.more || x.active)
        .map((x) => (
          <a href={x.path} class="tab" aria-current={x.active ? 'page' : undefined} onClick={go(x)}>
            {x.label}
          </a>
        ))}
      {extra.length ? (
        <details class="tab-more" ref={menu}>
          <summary class="tab">{t.more ?? 'More'} ▾</summary>
          <div class="menu">
            {extra.map((x) => (
              <a href={x.path} class="menu-item" onClick={go(x)}>
                {x.label}
              </a>
            ))}
          </div>
        </details>
      ) : null}
    </nav>
  );
}

/* ---------- shell ---------- */
export interface NavItem {
  path: string;
  label: string;
  icon: string;
  active: boolean;
  /** Section heading, shown only when the menu is long enough (NAV_HEADINGS_FROM rows). */
  group?: string;
  /** Rarely used items (Tickets) sit small at the foot of the menu. */
  foot?: boolean;
  /** Personal items (Settings) sit first in the account menu, top right, instead of the side menu (owner 2026-10-28). */
  account?: boolean;
  /** Preferred for the phone tab bar when the menu has more than TAB_SLOTS + 1 rows (owner 2026-11-01). */
  tab?: boolean;
}
/** A short menu (editors, camera, freelancers) reads best without headings (owner 2026-10-27). */
const NAV_HEADINGS_FROM = 5;
/** Phones get a tab bar at the bottom instead of the side menu (owner 2026-11-01): up to five tabs; a longer
 * menu shows four (the `tab` items first) plus "More", which opens the rest as tiles from the bottom. */
const TAB_SLOTS = 4;
export function splitTabs(items: NavItem[]): { tabs: NavItem[]; rest: NavItem[] } {
  if (items.length <= TAB_SLOTS + 1) return { tabs: items, rest: [] };
  const picked = new Set(items.filter((n) => n.tab).slice(0, TAB_SLOTS));
  for (const n of items) if (picked.size < TAB_SLOTS) picked.add(n);
  return { tabs: items.filter((n) => picked.has(n)), rest: items.filter((n) => !picked.has(n)) };
}
export interface Command {
  label: string;
  icon: string;
  group: string;
  hint?: string;
  run: () => void;
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
/** Same name, same hue – the prototype's avatar rule. */
export const hue = (name: string) => [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export function Shell(p: { name: string; subtitle: string; nav: NavItem[]; commands: Command[]; onSignOut: () => void; children: ComponentChildren; topbar?: ComponentChildren; onLang?: (l: Lang) => void; onViewAs?: () => void; menuItems?: MenuItem[] }) {
  const t = useT();
  const lang = useLang();
  const [moreOpen, setMoreOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const moreRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const here = typeof location === 'undefined' ? '' : location.pathname + location.search;

  const mainRef = useRef<HTMLElement>(null);
  const path = typeof location === 'undefined' ? '' : location.pathname;
  const firstPath = useRef(path);
  // the "More" sheet closes when the page changes
  useEffect(() => setMoreOpen(false), [here]);
  // a new page moves focus to its content, so a screen reader announces it; tabs inside the page keep their focus
  useEffect(() => {
    if (path === firstPath.current) return;
    firstPath.current = '';
    const main = mainRef.current;
    if (main && !main.contains(document.activeElement)) main.focus({ preventScroll: true });
  }, [path]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setMenuOpen(false);
        setPaletteOpen((o) => !o);
      } else if (e.key === 'Escape' && moreOpen) {
        setMoreOpen(false);
        moreRef.current?.focus();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [moreOpen]);
  useEffect(() => {
    if (moreOpen) sheetRef.current?.querySelector<HTMLElement>('a')?.focus();
  }, [moreOpen]);

  const pickTheme = (t: Theme) => {
    applyTheme(t);
    setTheme(t);
  };
  const commands = useMemo<Command[]>(
    () => [
      ...p.commands,
      { group: t.cmd.theme, label: t.cmd.light, icon: 'sun', run: () => pickTheme('light') },
      { group: t.cmd.theme, label: t.cmd.dark, icon: 'moon', run: () => pickTheme('dark') },
      { group: t.cmd.theme, label: t.cmd.system, icon: 'monitor', run: () => pickTheme('system') },
      ...(p.onLang ? [{ group: t.cmd.language, label: lang === 'sv' ? t.cmd.toEnglish : t.cmd.toSwedish, icon: 'globe', hint: 'language språk', run: () => p.onLang?.(lang === 'sv' ? 'en' : 'sv') }] : []),
      { group: t.cmd.account, label: t.cmd.signOut, icon: 'log-out', run: p.onSignOut }
    ],
    [p.commands, p.onSignOut, p.onLang, lang]
  );

  const main = p.nav.filter((n) => !n.foot && !n.account);
  const foot = p.nav.filter((n) => n.foot && !n.account);
  const accountNav = p.nav.filter((n) => n.account);
  const headings = main.length >= NAV_HEADINGS_FROM;
  // consecutive items with the same group form one section; without headings everything is one plain list
  const sections = main.reduce<{ label?: string; items: NavItem[] }[]>((acc, n) => {
    const label = headings ? n.group : undefined;
    const last = acc[acc.length - 1];
    if (last && last.label === label) last.items.push(n);
    else acc.push({ label, items: [n] });
    return acc;
  }, []);
  const go = (n: NavItem) => (e: Event) => (e.preventDefault(), navigate(n.path), setMoreOpen(false));
  const navLink = (n: NavItem) => (
    <a href={n.path} class="nav-item" aria-current={n.active ? 'page' : undefined} onClick={go(n)}>
      <Icon name={n.icon} />
      <span>{n.label}</span>
    </a>
  );
  const { tabs, rest } = splitTabs([...main, ...foot]);
  const restActive = rest.some((n) => n.active);

  return (
    <div class={`app${moreOpen ? ' more-open' : ''}`}>
      <a class="skip" href="#main">
        {t.shell.skip}
      </a>
      <header class="topbar">
        <a class="brand" href="/" aria-label="KFF Hub" onClick={(e) => (e.preventDefault(), navigate('/'))}>
          <span class="brand-mark" aria-hidden="true">
            KFF
          </span>
          <span class="brand-name">Hub</span>
        </a>
        <button type="button" class="search-btn" data-tour="search" aria-label={t.shell.searchLabel} aria-keyshortcuts="Control+K Meta+K" onClick={() => setPaletteOpen(true)}>
          <Icon name="search" />
          <span>{t.shell.searchBtn}</span>
          <kbd class="kbd">{isMac() ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
        <span class="topbar-spacer" />
        {p.topbar}
        <AccountMenu name={p.name} subtitle={p.subtitle} open={menuOpen} setOpen={setMenuOpen} theme={theme} pickTheme={pickTheme} onSignOut={p.onSignOut} onLang={p.onLang} onViewAs={p.onViewAs} items={p.menuItems} nav={accountNav} />
      </header>
      <div class="app-body">
        <nav class="sidenav" id="kff-nav" aria-label={t.nav.main}>
          {sections.map((s) =>
            s.label ? (
              <div class="nav-group" role="group" aria-label={s.label}>
                <p class="nav-heading" aria-hidden="true">
                  {s.label}
                </p>
                {s.items.map(navLink)}
              </div>
            ) : (
              s.items.map(navLink)
            )
          )}
          {foot.length ? <div class="nav-foot">{foot.map(navLink)}</div> : null}
        </nav>
        <main id="main" class="main" tabIndex={-1} ref={mainRef}>
          {p.children}
        </main>
      </div>
      <nav class="tabbar" aria-label={t.nav.main}>
        {tabs.map((n) => (
          <a href={n.path} class="tabbar-item" aria-current={n.active && !moreOpen ? 'page' : undefined} onClick={go(n)}>
            <Icon name={n.icon} />
            <span>{n.label}</span>
          </a>
        ))}
        {rest.length ? (
          <button type="button" class="tabbar-item" ref={moreRef} aria-expanded={moreOpen} aria-controls="kff-more" data-active={restActive || moreOpen ? 'true' : undefined} onClick={() => setMoreOpen((o) => !o)}>
            <Icon name="more" />
            <span>{t.shell.more}</span>
          </button>
        ) : null}
      </nav>
      {moreOpen ? (
        <>
          <div class="more-scrim" aria-hidden="true" onClick={() => setMoreOpen(false)} />
          <div class="more-sheet" id="kff-more" role="dialog" aria-label={t.shell.more} ref={sheetRef}>
            <div class="more-grid">
              {rest.map((n) => (
                <a href={n.path} class="more-tile" aria-current={n.active ? 'page' : undefined} onClick={go(n)}>
                  <Icon name={n.icon} />
                  <span>{n.label}</span>
                </a>
              ))}
            </div>
          </div>
        </>
      ) : null}
      {paletteOpen ? <CommandPalette commands={commands} onClose={() => setPaletteOpen(false)} /> : null}
    </div>
  );
}

/** An extra line in the account menu (above Sign out). */
export interface MenuItem {
  label: string;
  icon: string;
  onClick: () => void;
}

function AccountMenu(p: { name: string; subtitle: string; open: boolean; setOpen: (o: boolean) => void; theme: Theme; pickTheme: (t: Theme) => void; onSignOut: () => void; onLang?: (l: Lang) => void; onViewAs?: () => void; items?: MenuItem[]; nav?: NavItem[] }) {
  const t = useT();
  const lang = useLang();
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!p.open) return;
    wrap.current?.querySelector<HTMLElement>('[role="menu"] a, [role="menu"] button')?.focus();
    const onDown = (e: Event) => {
      if (!wrap.current?.contains(e.target as Node)) p.setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      p.setOpen(false);
      btn.current?.focus();
    };
    addEventListener('pointerdown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('pointerdown', onDown);
      removeEventListener('keydown', onKey);
    };
  }, [p.open]);
  const themes: [Theme, string, string][] = [
    ['light', t.shell.themes.light, 'sun'],
    ['dark', t.shell.themes.dark, 'moon'],
    ['system', t.shell.themes.system, 'monitor']
  ];
  // each language in its own words, so the way back is readable whichever one is on
  const langs: [Lang, string][] = [
    ['en', 'English'],
    ['sv', 'Svenska']
  ];
  return (
    <div class="menu-wrap" ref={wrap}>
      <button type="button" class="me-btn" ref={btn} aria-haspopup="menu" aria-expanded={p.open} aria-label={t.shell.account(p.name)} onClick={() => p.setOpen(!p.open)}>
        <span class="avatar" style={`--h:${hue(p.name)}`} aria-hidden="true">
          {initials(p.name)}
        </span>
      </button>
      {p.open ? (
        <div class="menu" role="menu" aria-label={t.shell.accountMenu}>
          <div class="menu-head">
            <strong>{p.name}</strong>
            <span class="small muted">{p.subtitle}</span>
          </div>
          <div class="menu-sep" role="separator" />
          {p.nav?.length ? (
            <>
              {p.nav.map((n) => (
                <a key={n.path} href={n.path} role="menuitem" class="menu-item" aria-current={n.active ? 'page' : undefined} onClick={(e) => (e.preventDefault(), p.setOpen(false), navigate(n.path))}>
                  <Icon name={n.icon} />
                  {n.label}
                </a>
              ))}
              <div class="menu-sep" role="separator" />
            </>
          ) : null}
          <div class="menu-label" id="theme-label">
            {t.shell.theme}
          </div>
          <div class="theme-seg" role="group" aria-labelledby="theme-label">
            {themes.map(([th, label, icon]) => (
              <button type="button" role="menuitemradio" aria-checked={p.theme === th} class="theme-opt" onClick={() => p.pickTheme(th)}>
                <Icon name={icon} />
                {label}
              </button>
            ))}
          </div>
          {p.onLang ? (
            <>
              <div class="menu-label" id="lang-label">
                {t.shell.language}
              </div>
              <div class="theme-seg" role="group" aria-labelledby="lang-label">
                {langs.map(([l, label]) => (
                  <button type="button" role="menuitemradio" aria-checked={lang === l} class="theme-opt" lang={l} onClick={() => p.onLang?.(l)}>
                    {label}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          <div class="menu-sep" role="separator" />
          {p.items?.map((it) => (
            <button key={it.label} type="button" role="menuitem" class="menu-item" onClick={() => (p.setOpen(false), it.onClick())}>
              <Icon name={it.icon} />
              {it.label}
            </button>
          ))}
          {p.onViewAs ? (
            <button
              type="button"
              role="menuitem"
              class="menu-item"
              onClick={() => {
                p.setOpen(false);
                p.onViewAs?.();
              }}
            >
              <Icon name="eye" />
              {t.shell.viewAs}
            </button>
          ) : null}
          <button type="button" role="menuitem" class="menu-item" onClick={p.onSignOut}>
            <Icon name="log-out" />
            {t.shell.signOut}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Normalise for search: lowercase, no diacritics (the prototype's rule). */
const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

export function CommandPalette(p: { commands: Command[]; onClose: () => void }) {
  const t = useT();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    return p.commands.filter((c) => words.every((w) => norm(`${c.label} ${c.group} ${c.hint ?? ''}`).includes(w)));
  }, [q, p.commands]);
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    input.current?.focus();
    return () => back?.focus();
  }, []);
  useEffect(() => setSel(0), [q]);
  const run = (c: Command | undefined) => {
    if (!c) return;
    p.onClose();
    c.run();
  };
  const onKey = (e: KeyboardEvent) => {
    const moves: Record<string, () => void> = {
      ArrowDown: () => setSel((s) => Math.min(s + 1, hits.length - 1)),
      ArrowUp: () => setSel((s) => Math.max(s - 1, 0)),
      Enter: () => run(hits[sel]),
      Escape: () => p.onClose(),
      Tab: () => {} // focus stays in the field; arrows move the selection
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    move();
  };
  let group = '';
  return (
    <div class="modal-backdrop modal-backdrop--top" role="presentation" onClick={(e) => e.target === e.currentTarget && p.onClose()}>
      <div class="modal modal--palette" role="dialog" aria-modal="true" aria-label={t.shell.palette}>
        <div class="palette-search">
          <Icon name="search" />
          <input
            ref={input}
            class="palette-input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={hits[sel] ? `pal-${sel}` : undefined}
            aria-autocomplete="list"
            placeholder={t.shell.palettePlaceholder}
            value={q}
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            onKeyDown={onKey}
          />
        </div>
        <ul class="palette-list" id="palette-list" role="listbox" aria-label={t.shell.results}>
          {hits.length === 0 ? <li class="palette-empty">{t.shell.noMatch(q)}</li> : null}
          {hits.map((c, i) => {
            const head = c.group !== group ? (group = c.group) : null;
            return (
              <>
                {head ? (
                  <li class="palette-group" role="presentation">
                    {head}
                  </li>
                ) : null}
                <li id={`pal-${i}`} role="option" aria-selected={i === sel} class="palette-item" onMouseMove={() => setSel(i)} onClick={() => run(c)}>
                  <Icon name={c.icon} />
                  <span>{c.label}</span>
                  {c.hint ? <span class="palette-hint">{c.hint}</span> : null}
                </li>
              </>
            );
          })}
        </ul>
        <div class="palette-foot" aria-hidden="true">
          <span>{t.shell.move}</span>
          <span>{t.shell.enter}</span>
          <span>{t.shell.esc}</span>
        </div>
      </div>
    </div>
  );
}
